import React from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { deliveryDeadline } from '../utils/reviewWindows';
import { useCountdown } from '../hooks/useCountdown';

interface DeliveryBadgeProps {
  video: { updated_at: string; status: string };
}

/**
 * Shows the 72-hour delivery countdown for an `under_review` video.
 * Returns null for any other status.
 *
 * Usage:
 * ```tsx
 * <DeliveryBadge video={video} />
 * ```
 */
export const DeliveryBadge: React.FC<DeliveryBadgeProps> = ({ video }) => {
  const { t } = useI18n();
  const deadline = deliveryDeadline(video);
  const { isOverdue, hours, minutes } = useCountdown(deadline);

  if (video.status !== 'under_review') return null;

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
      isOverdue
        ? 'bg-red-500/15 border border-red-500/20 text-red-400'
        : 'bg-blue-500/10 border border-blue-500/20 text-blue-400'
    }`}>
      {isOverdue ? <AlertTriangle size={12} /> : <Clock size={12} />}
      {isOverdue
        ? t('manage_reviews.overdue', { h: hours })
        : t('manage_reviews.time_to_deliver', { h: hours, m: minutes })
      }
    </div>
  );
};
